import { create } from 'zustand';

import { applyDocumentLocale, changeLocale } from '@/i18n';
import type { Locale } from '@/i18n';
import {
    ApiRequestError,
    del,
    ensureCsrfCookie,
    get,
    onSessionEnded,
    post,
} from '@/lib/api';
import type { ApiResource, AuthUser, Bootstrap } from '@/types';

/**
 * Session and application-shell state.
 *
 * Zustand rather than TanStack Query for this one slice: the bootstrap payload is
 * not a cached server resource that screens re-fetch, it is the ambient context
 * every screen reads synchronously — permissions, branding, locale. Modelling it
 * as a query would mean every permission check became a hook subscription with a
 * loading state, which is the wrong shape for "may this user see this button".
 *
 * Server-owned data belongs in TanStack Query. This store holds only what the
 * shell itself needs.
 */

interface AuthState {
    bootstrap: Bootstrap | null;
    user: AuthUser | null;
    ready: boolean;
    /** Set when the session ended server-side, so the login screen can explain why. */
    endedReason: 'timeout' | 'unauthenticated' | null;

    initialise: (initial: Bootstrap) => void;
    refresh: () => Promise<void>;
    signIn: (
        identifier: string,
        password: string,
        remember?: boolean,
    ) => Promise<void>;
    signOut: () => Promise<void>;
    setLocale: (locale: Locale) => Promise<void>;
    clearEndedReason: () => void;

    /** Does the signed-in user hold this permission? */
    can: (permission: string) => boolean;
    /** Any one of these permissions. */
    canAny: (...permissions: string[]) => boolean;
    hasRole: (role: string) => boolean;
}

/**
 * Apply the server's brand tokens to the document.
 *
 * The shell already inlines these on first paint; this re-applies them after an
 * administrator saves new branding, so the change is visible immediately rather
 * than on next reload.
 */
function applyBranding(bootstrap: Bootstrap): void {
    const existing = document.getElementById('dbcx-brand-tokens');
    const style = existing ?? document.createElement('style');

    style.id = 'dbcx-brand-tokens';
    style.textContent = `:root { ${bootstrap.branding.css_variables} }`;

    if (!existing) {
        document.head.appendChild(style);
    }

    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

    if (favicon && bootstrap.branding.favicon_url) {
        favicon.href = bootstrap.branding.favicon_url;
    }

    document.title = bootstrap.branding.app_name;
}

export const useAuthStore = create<AuthState>((set, getState) => ({
    bootstrap: null,
    user: null,
    ready: false,
    endedReason: null,

    initialise: (initial) => {
        applyBranding(initial);
        applyDocumentLocale(initial.locale);

        set({ bootstrap: initial, user: initial.user, ready: true });
    },

    refresh: async () => {
        try {
            const { data } = await get<ApiResource<Bootstrap>>('/session');

            applyBranding(data);

            set({ bootstrap: data, user: data.user, ready: true });
        } catch (error) {
            // A 401 here is the normal answer for an anonymous visitor — the
            // kiosk, the public complaint status page, the login screen. It means
            // "no session", not "something failed".
            if (error instanceof ApiRequestError && error.isUnauthenticated) {
                set({ user: null, ready: true });

                return;
            }

            throw error;
        }
    },

    signIn: async (identifier, password, remember = false) => {
        // Must precede the POST: without the XSRF cookie, Laravel rejects it as a
        // CSRF failure (419) before it ever reaches the controller.
        await ensureCsrfCookie();

        const { data } = await post<ApiResource<Bootstrap>>('/session', {
            email: identifier,
            password,
            remember,
        });

        applyBranding(data);

        set({
            bootstrap: data,
            user: data.user,
            ready: true,
            endedReason: null,
        });

        // Adopt the user's stored language preference on sign-in, so a branch
        // officer who works in Arabic gets Arabic even on a shared terminal last
        // used in English.
        if (data.user?.locale && data.user.locale !== data.locale) {
            await changeLocale(data.user.locale);
        }
    },

    signOut: async () => {
        try {
            await del('/session');
        } finally {
            // Cleared regardless of whether the request succeeded. If the network
            // is down, the user still expects to be signed out locally rather than
            // left looking at their own data.
            set({ user: null, endedReason: null });
        }
    },

    setLocale: async (locale) => {
        await changeLocale(locale);

        const { bootstrap } = getState();

        if (bootstrap) {
            set({
                bootstrap: {
                    ...bootstrap,
                    locale,
                    direction: locale === 'ar' ? 'rtl' : 'ltr',
                },
            });
        }
    },

    clearEndedReason: () => set({ endedReason: null }),

    /*
     * Permission checks are a UI convenience only. Every endpoint authorises
     * independently — hiding a button protects nothing from anyone willing to
     * call the API directly, and treating this as security would be a mistake.
     */
    can: (permission) =>
        getState().user?.permissions.includes(permission) ?? false,

    canAny: (...permissions) => {
        const held = getState().user?.permissions;

        return held
            ? permissions.some((permission) => held.includes(permission))
            : false;
    },

    hasRole: (role) => getState().user?.roles.includes(role) ?? false,
}));

/*
 * React to the session ending server-side, from wherever it was detected.
 * Registered once at module load; the api module owns the detection, this owns
 * the state change.
 */
onSessionEnded((reason) => {
    useAuthStore.setState({ user: null, endedReason: reason });
});

/** Convenience selectors — keep components from subscribing to the whole store. */
export const useUser = () => useAuthStore((state) => state.user);
export const useBranding = () =>
    useAuthStore((state) => state.bootstrap?.branding);
export const useIsDemoMode = () =>
    useAuthStore((state) => state.bootstrap?.demo.enabled ?? false);
